import { Component, Event, EventEmitter, Fragment, h, Prop } from '@stencil/core';
import { ClickOutside } from '../../../utils/stencil/clickOutside';
import { Article } from '../rentware-cart-article/interfaces';
import { CartStatus, ComponentView, Price, TotalPriceCalculation } from '../../../types/types';
import { ShippingItem, SplitOption } from '../../../redux/types';
import { ButtonAppearance, ButtonSize, ButtonStyle } from '../../atoms/rentware-button/types';
import { AccordionNode } from '../../../components';

@Component({
  tag: 'rentware-cart-template',
  styleUrl: 'style.sass',
  shadow: true,
})
export class RentwareCartTemplate {
  @Event() ClickOutsideCart: EventEmitter;

  @ClickOutside()
  someMethod({ target }) {
    this.ClickOutsideCart.emit({ target });
  }

  @Prop() cartStatus: CartStatus;
  @Prop() showVariationModalForId: string;
  @Prop() showMileageModalForId: string;
  @Prop() bookingId: string;
  @Prop() buttonIconTotal;
  @Prop() buttonIconPrice: Price;
  @Prop() cartPriceCalculation: TotalPriceCalculation;

  @Prop() headerTitle?: string;
  @Prop() totalPrice?: Price;
  @Prop() totalIntervalPrice?: Price;
  @Prop() depositPrice?: Price;
  @Prop() buttonText?: string;
  @Prop() emptyTitleText: string;
  @Prop() emptyHintText: string;
  @Prop() expiredTitleText: string;
  @Prop() expiredHintText: string;
  @Prop() buttonNextStepText: string;
  @Prop() backToCartText: string;
  @Prop() articlesText: string;
  @Prop() distanceUnitAbbreviation: string;

  @Prop() showDate?: boolean;
  @Prop() pickUpDate?: Date;
  @Prop() dropOffDate?: Date;
  @Prop() pickUpTime?: string;
  @Prop() dropOffTime?: string;
  @Prop() articles?: Article[];
  @Prop() shipping?: ShippingItem;

  @Prop() onClose?;
  @Prop() onButton?;
  @Prop() onVariationSubmit;
  @Prop() onMileageOptionChange;
  @Prop() cartView: ComponentView;
  @Prop() checkoutNextClick;
  @Prop() waitingArticles = [];
  @Prop() onShowVariationModalForId;
  @Prop() splitOption: SplitOption;

  get cartStyle() {
    if (this.cartView === ComponentView.Checkout) {
      return 'CartTemplateContainer InsideCheckout';
    }

    let style = 'GlobalButtonPosition CartTemplateContainer';

    if (this.cartStatus === CartStatus.Active) {
      style += ' CartWithContent';
    } else {
      style += ' CartEmpty';
    }

    return style;
  }

  get modalStyle() {
    if (this.cartView === ComponentView.Checkout) {
      return 'CartTemplateModal InsideCheckoutModal OpenModal';
    }

    return 'CartTemplateModal OpenModal';
  }

  get buttonAppearance(): ButtonAppearance {
    if (this.cartStatus === CartStatus.Active) {
      return {
        fullWidth: true,
        size: ButtonSize.XL,
      };
    } else {
      return {
        style: ButtonStyle.Inverse,
        fullWidth: true,
        size: ButtonSize.XL,
      };
    }
  }

  get cartNodes(): AccordionNode[] {
    return [
      {
        expanded: <rentware-cart-template-timer />,
        collapsed: <rentware-expiration-timer component={ComponentView.Checkout} />,
        hideCollapsedOnExpanded: true,
      },
      {
        expanded: (
          <rentware-cart-template-articles
            cartPriceCalculation={this.cartPriceCalculation}
            showVariationModalForId={this.showVariationModalForId}
            showMileageModalForId={this.showMileageModalForId}
            articles={this.articles}
            shipping={this.shipping}
            onVariationSubmit={this.onVariationSubmit}
            onMileageOptionChange={this.onMileageOptionChange}
            waitingArticles={this.waitingArticles}
            onShowVariationModalForId={this.onShowVariationModalForId}
          />
        ),
        collapsed: <span class="RTCDAccordionRow">{this.articlesText}</span>,
      },
    ];
  }

  render() {
    return (
      <Fragment>
        <div class={this.cartStyle}>
          <div class="InnerContainer">
            <rentware-cart-header headerTitle={this.headerTitle} onClose={this.onClose} showTimer={false} />

            {this.cartStatus === CartStatus.Active && (
              <Fragment>
                {this.showDate && (
                  <Fragment>
                    <rentware-spacer size="S" />

                    <rentware-date-output size="M" />
                  </Fragment>
                )}

                <rentware-spacer size="M" />

                <div class="CartContent">
                  <rtr-accordion startingIndex={1} nodes={this.cartNodes} view={ComponentView.Checkout} />
                </div>

                <rentware-spacer size="M" />

                <div class="Summary">
                  <rentware-total-vat price={this.totalPrice} />
                  <span class="Total">
                    <rentware-total-price price={this.totalPrice} />
                  </span>
                </div>

                {this.depositPrice?.gross > 0 && (
                  <div class="SummaryDeposit">
                    <rentware-deposit-price price={this.depositPrice} />
                  </div>
                )}
              </Fragment>
            )}

            {this.cartStatus === CartStatus.Empty && <empty-cart-template titleText={this.emptyTitleText} hintText={this.emptyHintText} />}

            {this.cartStatus === CartStatus.Expired && <expired-cart-template titleText={this.expiredTitleText} hintText={this.expiredHintText} />}

            {(this.cartView === ComponentView.Default || this.cartView === ComponentView.Details) && (
              <Fragment>
                <rentware-spacer size="M" />
                <rentware-button appearance={this.buttonAppearance} buttonText={this.buttonText} onClick={this.onButton} />
                <rentware-spacer size="S" />
                {this.cartView === ComponentView.Default && (
                    <rentware-secure secureTitle={this.headerTitle} goBackText={this.backToCartText} />
                )}
              </Fragment>
            )}

            {this.cartView === ComponentView.Checkout && (
              <Fragment>
                <rentware-spacer size="M" />

                <div class="ActionContainer">
                  <div class="CartContainer">
                    <rentware-cart-control-button-checkout
                      total={this.buttonIconTotal}
                      price={this.buttonIconPrice}
                      onClick={this.onClose}
                      isAlreadyOpened={true}
                      splitOption={this.splitOption}
                      depositPrice={this.depositPrice}
                    />
                  </div>

                  <div class="ButtonContainer">
                    <rentware-button buttonText={this.buttonNextStepText} onClick={this.checkoutNextClick} />
                  </div>
                </div>
              </Fragment>
            )}
          </div>
        </div>

        <div class={this.modalStyle} onClick={this.onClose} />
      </Fragment>
    );
  }
}
